import { useEffect, useState } from 'react';

// PRIME-MOB-UX-001 (locked decision 1): the mobile shell engages at or below a
// 768px viewport width. This module is the SINGLE source of that breakpoint —
// CSS media queries and the JS shell behavior both derive from these two
// exports so the value can never drift between layers.
export const MOBILE_SHELL_MAX_WIDTH = 768;
export const MOBILE_SHELL_MEDIA_QUERY = `(max-width: ${MOBILE_SHELL_MAX_WIDTH}px)`;

function readMatch() {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia(MOBILE_SHELL_MEDIA_QUERY).matches;
}

/**
 * Reactive boolean: true while the viewport is in mobile-shell mode
 * (≤ MOBILE_SHELL_MAX_WIDTH). Use ONLY where JS behavior must diverge from the
 * desktop shell (drawer state, body-scroll lock, moving controls into menus).
 * Pure presentation should stay CSS-first via MOBILE_SHELL_MEDIA_QUERY so there
 * is no first-paint flash.
 *
 * SSR/degraded environments (no window / no matchMedia) resolve to false, which
 * keeps the classic desktop shell as the safe default.
 */
export function useIsMobileShell() {
  const [isMobile, setIsMobile] = useState(readMatch);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const mql = window.matchMedia(MOBILE_SHELL_MEDIA_QUERY);
    const onChange = (event) => setIsMobile(event.matches);
    // Re-sync immediately in case the viewport crossed the breakpoint between
    // the initial useState read and effect attachment.
    setIsMobile(mql.matches);
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', onChange);
    } else if (typeof mql.addListener === 'function') {
      // Safari < 14 / older WebKit.
      mql.addListener(onChange);
    }
    return () => {
      if (typeof mql.removeEventListener === 'function') {
        mql.removeEventListener('change', onChange);
      } else if (typeof mql.removeListener === 'function') {
        mql.removeListener(onChange);
      }
    };
  }, []);

  return isMobile;
}
